import { createContext, useContext } from "react";
import type { SharedValue } from "@-/com.npmjs.react-native-reanimated";
import type { LearnDeckSurface } from "@-/dev.improve.haniverse.lib.learn.deck";

/**
 * Where a piece of card content is being rendered.
 *
 * Shared drill/lesson/panel components render in three situations: as the ACTIVE card's surface,
 * as an inert background surface (the prepared successor, or the hidden face of the current card),
 * or outside the deck entirely (Browse, tests). Anything that must only ever happen for the
 * learner's live surface — focusing an input, playing audio, registering a voice turn, publishing
 * the header — reads `isActive` here. The default is fully active so components outside the deck
 * keep behaving as before.
 */
export type LearnCardSurfaceContextValue = {
  /**
   * UI-thread flag: this card's FRONT is on show — it owns the stage, or it is the successor an
   * exit has begun to uncover. Absent outside the deck and on the other surfaces. Stroke players
   * take it as their `playSignal` so drawing starts with the uncovering, not with React.
   */
  frontVisibleSignal?: SharedValue<boolean>;
  /** This subtree belongs to the current card AND is its visible face / view. */
  isActive: boolean;
  /** This subtree belongs to the current (top) card, whichever face is showing. */
  isCurrent: boolean;
  presentationKey: string | null;
  /** Call once the surface has rendered its essential content and laid out. Idempotent. */
  reportReady: () => void;
  surface: LearnDeckSurface | null;
};

export const LearnCardSurfaceContext = createContext<LearnCardSurfaceContextValue>({
  isActive: true,
  isCurrent: true,
  presentationKey: null,
  reportReady: () => {},
  surface: null,
});

export function useLearnCardSurface(): LearnCardSurfaceContextValue {
  return useContext(LearnCardSurfaceContext);
}

/**
 * Whether this subtree is inside a Learn deck. A plain boolean context (rather than probing
 * `LearnDeckContext`, whose default throws on access) so shared components can route a reveal or
 * a rating through the deck when one exists and keep their standalone behaviour otherwise.
 */
export const LearnDeckPresenceContext = createContext(false);

export function useIsInsideLearnDeck(): boolean {
  return useContext(LearnDeckPresenceContext);
}
